/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

:host {
  display: block;
  font-family: $font-family-base;

  * {
    box-sizing: border-box;
    outline: 0 none;
    text-rendering: optimizelegibility;
  }
}

.#{$prefix}offset-teaser-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  .#{$prefix}offset-teaser-image-wrapper {
    position: relative;
    width: 100%;
    display: flex;

    .#{$prefix}offset-teaser-image-inner {
      position: relative;

      &::after {
        @include aspect-ratio(2, 1);
      }
    }
  }

  .#{$prefix}offset-teaser-content-box-wrapper {
    position: relative;
    width: 100%;
    display: flex;
    flex-grow: 1;

    &.#{$prefix}without-image {
      flex-direction: column;
    }

    &.#{$prefix}overlap {
      margin-top: -160px;
    }

    .#{$prefix}offset-teaser-content-box-inner {
      display: flex;
      flex-direction: column;
      position: relative;
      padding: 48px 48px 48px 48px;
      background: v(backgroundColor);
      height: 100%;

      &.#{$prefix}gradient {
        background: v(gradientColor);
      }

      &.#{$prefix}split {
        display: flex;
        flex-direction: row;
        gap: 48px;

        .#{$prefix}offset-teaser-content-box-inner-split {
          position: relative;
          width: 50%;
          display: flex;
          flex-direction: column;
        }
      }

      .#{$prefix}offset-teaser-content {
        flex-grow: 1;

        > *:not(:last-child) {
          display: flex;
          margin-bottom: 40px;
        }

        > slot[name="content"],
        > slot {
          display: flex;
          flex-direction: column;
          gap: 24px;
          margin-bottom: 0;

          @include for-size("phone") {
            gap: 16px;
          }
        }
      }

      .#{$prefix}offset-teaser-cta {
        margin-top: 80px;

        > slot[name="cta"] {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
      }
    }
  }

  /*
   * ALIGNMENT HANDLING
   */
  &.#{$prefix}content-box-alignment-left {
    .#{$prefix}offset-teaser-image-wrapper {
      justify-content: flex-end;
    }

    .#{$prefix}offset-teaser-content-box-wrapper {
      justify-content: flex-start;
    }
  }

  &.#{$prefix}content-box-alignment-right {
    .#{$prefix}offset-teaser-image-wrapper {
      justify-content: flex-start;
    }

    .#{$prefix}offset-teaser-content-box-wrapper {
      justify-content: flex-end;
    }
  }

  /*
   * SIZE HANDLING
   */
  &.#{$prefix}image-size-large {
    .#{$prefix}offset-teaser-image-inner {
      width: 82.78%;
    }

    .#{$prefix}offset-teaser-content-box-inner {
      width: 65.56%;
    }
  }

  &.#{$prefix}image-size-full-width {
    .#{$prefix}offset-teaser-image-inner {
      width: 100%;
    }

    .#{$prefix}offset-teaser-content-box-inner {
      width: 65.56%;
    }
  }

  &.#{$prefix}image-size-small-no-offset {
    display: grid;
    grid-template-columns: repeat(12, [col-start] 1fr);

    .#{$prefix}offset-teaser-image-wrapper {
      display: block;
      grid-column: col-start / span 5;
      grid-row: 1;
      width: 100%;
      z-index: 4;
    }

    .#{$prefix}offset-teaser-content-box-wrapper {
      grid-column: 5 col-start / -1;
      grid-row: 1;
      padding-left: calc(100% / 7);
      padding-right: 0;
      width: 100%;
      position: relative;

      &.#{$prefix}overlap {
        margin-top: 0 !important;
      }
    }

    .#{$prefix}offset-teaser-image-inner {
      width: 100%;

      &::after {
        @include aspect-ratio(1, 1);
      }
    }

    .#{$prefix}offset-teaser-content-box-wrapper {
      background: v(backgroundColor);

      &:has(.#{$prefix}gradient) {
        background: v(gradientColor);
      }

      .#{$prefix}offset-teaser-content-box-inner {
        width: 100%;
        background: none;
      }
    }

    &.#{$prefix}content-box-alignment-left {
      .#{$prefix}offset-teaser-image-wrapper {
        grid-column: 8 / -1;
      }

      .#{$prefix}offset-teaser-content-box-wrapper {
        grid-column: col-start / 9;
        padding-right: calc(100% / 8);
        padding-left: 0;
        grid-row: 1;
      }
    }
  }
}

:host(.container-breakpoint-1200) {
  .#{$prefix}offset-teaser-wrapper {
    .#{$prefix}offset-teaser-content-box-wrapper {
      &.#{$prefix}overlap {
        margin-top: -120px;
      }

      .#{$prefix}offset-teaser-content-box-inner {
        padding: 40px 40px 40px 40px;

        &.#{$prefix}split {
          gap: 40px;
        }

        .#{$prefix}offset-teaser-cta {
          margin-top: 72px;
        }
      }
    }

    .#{$prefix}offset-teaser-image-inner {
      &::after {
        @include aspect-ratio(4, 3);
      }
    }

    /*
     * SIZE HANDLING
     */
    &.#{$prefix}image-size-large {
      .#{$prefix}offset-teaser-image-inner {
        width: 82.75%;
      }

      .#{$prefix}offset-teaser-content-box-inner {
        width: 74.17%;
      }
    }

    &.#{$prefix}image-size-full-width {
      .#{$prefix}offset-teaser-image-inner {
        width: 100%;
      }

      .#{$prefix}offset-teaser-content-box-inner {
        width: 82.75%;
      }
    }

    &.#{$prefix}image-size-small-no-offset {
      .#{$prefix}offset-teaser-image-inner {
        &::after {
          @include aspect-ratio(1, 1);
        }
      }
    }
  }
}

:host(.container-breakpoint-800) {
  .#{$prefix}offset-teaser-wrapper {
    .#{$prefix}offset-teaser-content-box-wrapper {
      &.#{$prefix}overlap {
        margin-top: -88px;
      }

      .#{$prefix}offset-teaser-content-box-inner {
        padding: 32px 32px 32px 32px;

        &.#{$prefix}split {
          gap: 32px;
        }

        .#{$prefix}offset-teaser-cta {
          margin-top: 64px;
        }
      }
    }

    .#{$prefix}offset-teaser-image-inner {
      &::after {
        @include aspect-ratio(4, 3);
      }
    }

    /*
     * SIZE HANDLING
     */
    &.#{$prefix}image-size-small {
      .#{$prefix}offset-teaser-image-inner {
        width: 75%;

        &::after {
          @include aspect-ratio(1, 1);
        }
      }

      .#{$prefix}offset-teaser-content-box-inner {
        width: 75%;
      }
    }

    &.#{$prefix}image-size-small-no-offset {
      .#{$prefix}offset-teaser-image-inner {
        &::after {
          @include aspect-ratio(1, 1);
        }
      }
    }

    &.#{$prefix}image-size-large {
      .#{$prefix}offset-teaser-image-inner {
        width: 91.41%;
      }

      .#{$prefix}offset-teaser-content-box-inner {
        width: 82.81%;
      }
    }

    &.#{$prefix}image-size-full-width {
      .#{$prefix}offset-teaser-image-inner {
        width: 100%;
      }

      .#{$prefix}offset-teaser-content-box-inner {
        width: 82.81%;
      }
    }
  }
}

:host(.container-breakpoint-400) {
  .#{$prefix}offset-teaser-wrapper {
    .#{$prefix}offset-teaser-content-box-wrapper {
      padding-left: 0;
      padding-right: 0;

      &.#{$prefix}overlap {
        margin-top: -48px;
      }

      .#{$prefix}offset-teaser-content-box-inner {
        padding: 24px 24px 24px 24px;

        &.#{$prefix}split {
          gap: 40px;
          flex-direction: column;

          .#{$prefix}offset-teaser-content-box-inner-split {
            width: 100%;

            + .#{$prefix}offset-teaser-content-box-inner-split {
              flex: 1;
            }
          }
        }

        .#{$prefix}offset-teaser-cta {
          margin-top: 56px;
        }
      }
    }

    &.#{$prefix}image-size-full-width {
      .#{$prefix}offset-teaser-content-box-wrapper {
        &.#{$prefix}overlap {
          margin-top: 0;
        }
      }
    }

    .#{$prefix}offset-teaser-image-inner {
      &::after {
        @include aspect-ratio(1, 1);
      }
    }

    /*
     * SIZE HANDLING
     */
    &.#{$prefix}image-size-small {
      .#{$prefix}offset-teaser-image-inner {
        width: calc(100% - 16px);
      }

      .#{$prefix}offset-teaser-content-box-inner {
        width: calc(100% - 16px);
      }
    }

    &.#{$prefix}image-size-small-no-offset {
      display: block;

      .#{$prefix}offset-teaser-image-wrapper {
        position: relative;
      }

      .#{$prefix}offset-teaser-content-box-wrapper {
        &.#{$prefix}overlap {
          margin-top: 0;
        }
      }

      .#{$prefix}offset-teaser-image-inner {
        width: 100%;

        &::after {
          @include aspect-ratio(1, 1);
        }
      }

      .#{$prefix}offset-teaser-content-box-inner {
        width: 100%;
      }
    }

    &.#{$prefix}image-size-large {
      .#{$prefix}offset-teaser-image-inner {
        width: calc(100% - 16px);
      }

      .#{$prefix}offset-teaser-content-box-inner {
        width: calc(100% - 16px);
      }
    }

    &.#{$prefix}image-size-full-width {
      .#{$prefix}offset-teaser-image-inner {
        width: 100%;
      }

      .#{$prefix}offset-teaser-content-box-inner {
        width: 100%;
      }
    }
  }
}

:host(.container-breakpoint-largest) {
  .#{$prefix}offset-teaser-wrapper {
    &.#{$prefix}image-size-small {
      flex-direction: row;
      align-items: flex-start;
      padding-bottom: 88px;

      .#{$prefix}offset-teaser-image-wrapper {
        justify-content: flex-start;
        width: 41.66%;

        .#{$prefix}offset-teaser-image-inner {
          width: 100%;

          &::after {
            @include aspect-ratio(1, 1);
          }
        }
      }

      .#{$prefix}offset-teaser-content-box-wrapper {
        justify-content: flex-end;
        width: 58.33%;
        min-height: calc(100% + 88px);
        margin-top: 0;
        display: flex;
        flex-direction: column;
        margin-bottom: -88px;

        &::before {
          content: "";
          position: relative;
          width: 100%;
          height: 100%;
          min-height: 88px;
        }

        .#{$prefix}offset-teaser-content-box-inner {
          width: calc(100% + 88px);
          margin-left: -88px;
        }
      }

      /*
      * ALIGNMENT HANDLING
      */
      &.#{$prefix}content-box-alignment-left {
        flex-direction: row-reverse;

        .#{$prefix}offset-teaser-image-wrapper {
          justify-content: flex-start;
        }

        .#{$prefix}offset-teaser-content-box-wrapper {
          justify-content: flex-end;

          .#{$prefix}offset-teaser-content-box-inner {
            margin-left: unset;
            margin-right: -88px;
          }
        }
      }
    }
  }
}

:host(.container-breakpoint-1200) {
  .#{$prefix}offset-teaser-wrapper {
    &.#{$prefix}image-size-small {
      flex-direction: row;
      align-items: flex-start;
      padding-bottom: 72px;

      .#{$prefix}offset-teaser-image-wrapper {
        justify-content: flex-start;
        width: 50%;

        .#{$prefix}offset-teaser-image-inner {
          width: 100%;

          &::after {
            @include aspect-ratio(1, 1);
          }
        }
      }

      .#{$prefix}offset-teaser-content-box-wrapper {
        justify-content: flex-end;
        width: 50%;
        min-height: calc(100% + 72px);
        margin-top: 0;
        display: flex;
        flex-direction: column;
        margin-bottom: -72px;

        &::before {
          content: "";
          position: relative;
          width: 100%;
          height: 100%;
          min-height: 72px;
        }

        .#{$prefix}offset-teaser-content-box-inner {
          width: calc(100% + 72px);
          margin-left: -72px;
        }
      }

      /*
      * ALIGNMENT HANDLING
      */
      &.#{$prefix}content-box-alignment-left {
        flex-direction: row-reverse;

        .#{$prefix}offset-teaser-image-wrapper {
          justify-content: flex-start;
        }

        .#{$prefix}offset-teaser-content-box-wrapper {
          justify-content: flex-end;

          .#{$prefix}offset-teaser-content-box-inner {
            margin-left: unset;
            margin-right: -72px;
          }
        }
      }
    }
  }
}

::slotted(eon-ui-link) {
  --contentHoverColor: var(--linkHoverColor) !important;
  --contentFocusColor: var(--linkHoverColor) !important;
}

::slotted(eon-ui-linkList) {
  --linklistHoverColor: var(--linkHoverColor) !important;
  --contentFocusColor: var(--linkHoverColor) !important;
}
